/**
 * Dealer Statement — printable ledger report.
 *
 * Shows a full chronological statement for a selected dealer over a chosen
 * period: opening balance -> invoices (debit) + payments/credit notes
 * (credit) -> closing balance with a running-balance column.
 *
 * Print mode mirrors the ar-aging report: printElement clones the ref'd
 * container into a dedicated print window with the shared print.css applied.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { FileText } from "lucide-react";
import type { ColumnDef } from "@tanstack/react-table";

import { formatMoney, formatMoneySmart } from "@bizop/shared/currency";
import { formatDate } from "@bizop/shared/dates";
import { Badge, DataTable, KpiCard } from "@bizop/ui-kit";

import { PageHeader } from "../../../components/PageHeader";
import { useMockData } from "../../../lib/mockMode";
import { usePersistentMockState } from "../../../lib/usePersistentMockState";
import { mockDealers, type MockDealer } from "../../../lib/salesMock";
import {
  mockInvoices,
  mockPayments,
  mockCreditNotes,
  type MockInvoice,
  type MockPayment,
  type MockCreditNote,
} from "../../../lib/invoicingMock";
import { useAppSettings } from "../../../lib/appSettings";
import { ExportButton } from "../../../components/export";
import {
  downloadCsv,
  exportDateStamp,
  printElement,
  toCsv,
  type ExportColumn,
} from "../../../lib/export";
import {
  PeriodSelector,
  QuickRangeBar,
  quickRangeWindow,
  defaultPeriodWindow,
  type PeriodWindow,
  type QuickRange,
} from "../../../components/gl/PeriodSelector";

type DealerStatementSearch = {
  dealerId?: string;
};

export const Route = createFileRoute("/_app/reports/dealer-statement")({
  component: DealerStatementPage,
  validateSearch: (search: Record<string, unknown>): DealerStatementSearch => ({
    dealerId: typeof search.dealerId === "string" ? search.dealerId : undefined,
  }),
});

/* -------------------------------------------------------------------
 * Ledger row type
 * ------------------------------------------------------------------- */

type LedgerRow = {
  id: string;
  dateMs: number;
  type: "Invoice" | "Payment" | "Credit Note";
  reference: string;
  description: string;
  debitCents: bigint;
  creditCents: bigint;
  runningBalanceCents: bigint;
};

/* -------------------------------------------------------------------
 * Ledger builder (pure function -- no hooks)
 * ------------------------------------------------------------------- */

function buildLedger(
  dealerId: string,
  openingBalanceCents: bigint,
  fromMs: number,
  toMs: number,
  invoices: MockInvoice[],
  payments: MockPayment[],
  creditNotes: MockCreditNote[],
): {
  rows: LedgerRow[];
  openingCents: bigint;
  totalDebitCents: bigint;
  totalCreditCents: bigint;
  closingCents: bigint;
} {
  // Invoices for this dealer that are non-void, non-draft, issued in period.
  const invoicesForDealer = invoices.filter(
    (inv) =>
      inv.dealerId === dealerId &&
      inv.status !== "void" &&
      inv.status !== "draft" &&
      inv.issueDate >= fromMs &&
      inv.issueDate <= toMs,
  );

  // All invoice IDs belonging to this dealer (any status) -- used to filter
  // payments which are linked by invoiceId, not directly by dealerId.
  const dealerInvoiceIds = new Set(
    invoices
      .filter((inv) => inv.dealerId === dealerId)
      .map((inv) => inv._id),
  );

  // Non-voided payments linked to this dealer's invoices, received in period.
  const paymentsForDealer = payments.filter(
    (pay) =>
      dealerInvoiceIds.has(pay.invoiceId ?? "") &&
      pay.voidedAt === null &&
      pay.receivedAt >= fromMs &&
      pay.receivedAt <= toMs,
  );

  // Non-void credit notes for this dealer issued in period.
  const creditNotesForDealer = creditNotes.filter(
    (cn) =>
      cn.dealerId === dealerId &&
      cn.status !== "void" &&
      cn.issueDate >= fromMs &&
      cn.issueDate <= toMs,
  );

  // Merge and sort chronologically.
  type RawEvent =
    | { kind: "invoice"; dateMs: number; id: string }
    | { kind: "payment"; dateMs: number; id: string }
    | { kind: "credit_note"; dateMs: number; id: string };

  const events: RawEvent[] = [
    ...invoicesForDealer.map((inv) => ({
      kind: "invoice" as const,
      dateMs: inv.issueDate,
      id: inv._id,
    })),
    ...paymentsForDealer.map((pay) => ({
      kind: "payment" as const,
      dateMs: pay.receivedAt,
      id: pay._id,
    })),
    ...creditNotesForDealer.map((cn) => ({
      kind: "credit_note" as const,
      dateMs: cn.issueDate,
      id: cn._id,
    })),
  ];

  events.sort((a, b) => a.dateMs - b.dateMs);

  const invoiceMap = new Map(invoices.map((i) => [i._id, i]));
  const paymentMap = new Map(payments.map((p) => [p._id, p]));
  const creditNoteMap = new Map(creditNotes.map((c) => [c._id, c]));

  let running = openingBalanceCents;
  let totalDebit = 0n;
  let totalCredit = 0n;
  const rows: LedgerRow[] = [];

  for (const event of events) {
    if (event.kind === "invoice") {
      const inv = invoiceMap.get(event.id);
      if (!inv) continue;
      const amount = inv.totalAmountInBaseCents;
      running += amount;
      totalDebit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Invoice",
        reference: inv.invoiceNumber || "(draft)",
        description: inv.notes ?? `Invoice ${inv.invoiceNumber || ""}`.trim(),
        debitCents: amount,
        creditCents: 0n,
        runningBalanceCents: running,
      });
    } else if (event.kind === "payment") {
      const pay = paymentMap.get(event.id);
      if (!pay) continue;
      const amount = pay.amountInBaseCents;
      running -= amount;
      totalCredit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Payment",
        reference: pay.paymentNumber,
        description: pay.notes ?? `Payment via ${pay.paymentMethod}`,
        debitCents: 0n,
        creditCents: amount,
        runningBalanceCents: running,
      });
    } else {
      const cn = creditNoteMap.get(event.id);
      if (!cn) continue;
      const amount = cn.amountInBaseCents;
      running -= amount;
      totalCredit += amount;
      rows.push({
        id: event.id,
        dateMs: event.dateMs,
        type: "Credit Note",
        reference: cn.creditNoteNumber,
        description: cn.notes ?? cn.creditReason,
        debitCents: 0n,
        creditCents: amount,
        runningBalanceCents: running,
      });
    }
  }

  return {
    rows,
    openingCents: openingBalanceCents,
    totalDebitCents: totalDebit,
    totalCreditCents: totalCredit,
    closingCents: running,
  };
}

/* -------------------------------------------------------------------
 * Helper: build a descriptive export filename
 * ------------------------------------------------------------------- */

function statementFilename(
  dealerCode: string,
  fromMs: number,
  toMs: number,
  ext: "csv" | "xls",
): string {
  const fromStr = new Date(fromMs).toISOString().slice(0, 10);
  const toStr = new Date(toMs).toISOString().slice(0, 10);
  const slug = dealerCode.toLowerCase().replace(/[^a-z0-9]+/g, "-");
  return `bizop-statement-${slug}-${fromStr}-to-${toStr}.${ext}`;
}

/* -------------------------------------------------------------------
 * Page component
 * ------------------------------------------------------------------- */

function DealerStatementPage() {
  const isMock = useMockData();
  void isMock;

  const { dealerId: searchDealerId } = Route.useSearch();
  const { companyProfile } = useAppSettings();
  const [dealers] = usePersistentMockState<MockDealer[]>("dealers", mockDealers);
  const [invoices] = usePersistentMockState<MockInvoice[]>(
    "invoices",
    mockInvoices,
  );
  const [payments] = usePersistentMockState<MockPayment[]>(
    "payments",
    mockPayments,
  );
  const [creditNotes] = usePersistentMockState<MockCreditNote[]>(
    "creditNotes",
    mockCreditNotes,
  );
  const [dealerId, setDealerId] = useState<string>(
    searchDealerId ?? dealers[0]?._id ?? "",
  );

  // When the search param arrives after initial render (e.g. dealers
  // hydrate after mount), sync the selection once.
  const appliedSearchRef = useRef(false);
  useEffect(() => {
    if (
      searchDealerId &&
      !appliedSearchRef.current &&
      dealers.some((d) => d._id === searchDealerId)
    ) {
      setDealerId(searchDealerId);
      appliedSearchRef.current = true;
    }
  }, [searchDealerId, dealers]);
  const [period, setPeriod] = useState<PeriodWindow>(defaultPeriodWindow);
  const [quickRange, setQuickRange] = useState<QuickRange>("this_month");
  const printRef = useRef<HTMLDivElement>(null);

  const selectedDealer = useMemo(
    () => dealers.find((d) => d._id === dealerId) ?? null,
    [dealers, dealerId],
  );

  const { rows, openingCents, totalDebitCents, totalCreditCents, closingCents } =
    useMemo(() => {
      if (!selectedDealer) {
        return {
          rows: [],
          openingCents: 0n,
          totalDebitCents: 0n,
          totalCreditCents: 0n,
          closingCents: 0n,
        };
      }
      return buildLedger(
        selectedDealer._id,
        selectedDealer.openingBalanceBaseCents ?? 0n,
        period.fromDate,
        period.toDate,
        invoices,
        payments,
        creditNotes,
      );
    }, [
      selectedDealer,
      period.fromDate,
      period.toDate,
      invoices,
      payments,
      creditNotes,
    ]);

  function handleQuickRange(key: QuickRange, window: PeriodWindow | null) {
    setQuickRange(key);
    if (window) {
      setPeriod(window);
    } else {
      // "Custom" -- switch PeriodSelector into custom mode, keep existing dates
      setPeriod((prev) => ({ ...prev, key: "custom" }));
    }
  }

  const columns = useMemo<ColumnDef<LedgerRow>[]>(
    () => [
      {
        accessorKey: "dateMs",
        header: "Date",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="tabular-nums text-(--text-secondary)">
            {formatDate(row.original.dateMs, { style: "short" })}
          </span>
        ),
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }: { row: { original: LedgerRow } }) => {
          const t = row.original.type;
          return (
            <Badge
              variant={
                t === "Invoice"
                  ? "info"
                  : t === "Payment"
                    ? "success"
                    : "warning"
              }
              size="sm"
            >
              {t}
            </Badge>
          );
        },
      },
      {
        accessorKey: "reference",
        header: "Reference #",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono text-[12px] text-(--text-primary)">
            {row.original.reference}
          </span>
        ),
        meta: { mono: true },
      },
      {
        accessorKey: "description",
        header: "Description",
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="text-(--text-secondary) text-[12px] truncate max-w-[220px] block">
            {row.original.description}
          </span>
        ),
      },
      {
        accessorKey: "debitCents",
        header: "Debit",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono tabular-nums text-(--text-primary)">
            {row.original.debitCents > 0n
              ? formatMoney(row.original.debitCents, "BDT", { locale: "en-BD" })
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "creditCents",
        header: "Credit",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span className="font-mono tabular-nums text-(--text-secondary)">
            {row.original.creditCents > 0n
              ? formatMoney(row.original.creditCents, "BDT", { locale: "en-BD" })
              : "—"}
          </span>
        ),
      },
      {
        accessorKey: "runningBalanceCents",
        header: "Balance",
        meta: { numeric: true, mono: true },
        cell: ({ row }: { row: { original: LedgerRow } }) => (
          <span
            className={
              row.original.runningBalanceCents < 0n
                ? "font-mono tabular-nums font-semibold text-(--danger)"
                : "font-mono tabular-nums font-semibold text-(--text-primary)"
            }
          >
            {formatMoney(row.original.runningBalanceCents, "BDT", {
              locale: "en-BD",
            })}
          </span>
        ),
      },
    ],
    [],
  );

  function handleCsv(ext: "csv" | "xls") {
    type CsvRow = {
      date: string;
      type: string;
      reference: string;
      description: string;
      debit: bigint;
      credit: bigint;
      balance: bigint;
    };
    const csvRows: CsvRow[] = rows.map((r) => ({
      date: formatDate(r.dateMs, { style: "short" }),
      type: r.type,
      reference: r.reference,
      description: r.description,
      debit: r.debitCents,
      credit: r.creditCents,
      balance: r.runningBalanceCents,
    }));
    const cols: ExportColumn<CsvRow>[] = [
      { key: "date", label: "Date" },
      { key: "type", label: "Type" },
      { key: "reference", label: "Reference #" },
      { key: "description", label: "Description" },
      { key: "debit", label: "Debit (BDT)" },
      { key: "credit", label: "Credit (BDT)" },
      { key: "balance", label: "Running Balance (BDT)" },
    ];
    const filename = selectedDealer
      ? statementFilename(selectedDealer.code, period.fromDate, period.toDate, ext)
      : `bizop-dealer-statement-${exportDateStamp()}.${ext}`;
    downloadCsv(filename, toCsv(csvRows, cols));
  }

  function handlePrint() {
    printElement(printRef.current, {
      title: `Dealer Statement — ${selectedDealer?.displayName ?? ""}`,
      subtitle: `${companyProfile.displayName} · ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`,
    });
  }

  return (
    <div className="flex flex-col gap-6" ref={printRef}>
      {/* Print-only header (hidden in normal view via print.css) */}
      <div className="hidden print:block mb-4">
        <p className="text-[11px] uppercase tracking-wider text-(--text-tertiary)">
          {companyProfile.displayName}
        </p>
        <h1 className="text-xl font-semibold text-(--text-primary)">
          Dealer Statement
        </h1>
        {selectedDealer ? (
          <p className="text-[13px] text-(--text-secondary)">
            {selectedDealer.displayName}
            {selectedDealer.legalName
              ? ` · ${selectedDealer.legalName}`
              : ""}
          </p>
        ) : null}
        <p className="text-[12px] text-(--text-tertiary)">
          {formatDate(period.fromDate, { style: "medium" })} —{" "}
          {formatDate(period.toDate, { style: "medium" })}
        </p>
      </div>

      <PageHeader
        title="Dealer Statement"
        description={
          selectedDealer
            ? `Ledger for ${selectedDealer.displayName} · ${formatDate(period.fromDate, { style: "short" })} → ${formatDate(period.toDate, { style: "short" })}`
            : "Select a dealer to view their statement."
        }
        actions={
          <ExportButton
            onCsv={() => handleCsv("csv")}
            onExcel={() => handleCsv("xls")}
            onPrint={handlePrint}
            disabled={!selectedDealer}
          />
        }
      />

      {/* Controls */}
      <div className="flex flex-col gap-3" data-print-hide="">
        {/* Row 1: Dealer selector + period selector */}
        <div className="flex flex-wrap items-center gap-4">
          {/* Dealer selector */}
          <div className="flex flex-col gap-1">
            <label
              htmlFor="ds-dealer"
              className="text-[11px] uppercase tracking-wide text-(--text-tertiary)"
            >
              Dealer
            </label>
            <select
              id="ds-dealer"
              value={dealerId}
              onChange={(e) => setDealerId(e.target.value)}
              className="h-8 rounded-md border border-(--border-subtle) bg-(--bg-canvas) px-2 text-[13px] text-(--text-primary) focus:outline-none focus:ring-2 focus:ring-(--accent)"
            >
              {dealers.map((d) => (
                <option key={d._id} value={d._id}>
                  {d.displayName} ({d.code})
                </option>
              ))}
            </select>
          </div>

          {/* Period selector (for Custom mode date inputs) */}
          {quickRange === "custom" ? (
            <PeriodSelector value={period} onChange={setPeriod} />
          ) : null}
        </div>

        {/* Row 2: Quick range bar */}
        <QuickRangeBar value={quickRange} onChange={handleQuickRange} />
      </div>

      {selectedDealer ? (
        <>
          {/* KPI strip */}
          <section
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4"
            aria-label="Statement KPIs"
          >
            <KpiCard
              caption="Opening balance"
              value={formatMoneySmart(openingCents, "BDT", { locale: "en-BD" })}
              hint="Carried forward"
            />
            <KpiCard
              caption="Total invoiced"
              value={formatMoneySmart(totalDebitCents, "BDT", {
                locale: "en-BD",
              })}
              hint="Period debits"
            />
            <KpiCard
              caption="Total paid"
              value={formatMoneySmart(totalCreditCents, "BDT", {
                locale: "en-BD",
              })}
              hint="Period credits"
            />
            <KpiCard
              caption="Closing balance"
              value={formatMoneySmart(closingCents, "BDT", { locale: "en-BD" })}
              hint="As of period end"
            />
          </section>

          {/* Ledger table */}
          <DataTable
            data={rows}
            columns={columns}
            getRowId={(r) => r.id}
            emptyTitle="No transactions in this period"
            emptyDescription="There are no invoices, payments, or credit notes for this dealer in the selected period."
            emptyIcon={<FileText className="h-5 w-5" aria-hidden />}
          />

          {/* Closing balance footer */}
          <div className="rounded-md border border-(--border-subtle) bg-(--bg-surface) px-4 py-3 flex items-center justify-between text-[13px]">
            <span className="text-(--text-secondary)">
              Closing balance:{" "}
              <span className="text-(--text-tertiary) text-[12px]">
                {formatDate(period.toDate, { style: "medium" })}
              </span>
            </span>
            <span
              className={
                closingCents < 0n
                  ? "font-mono tabular-nums font-semibold text-(--danger)"
                  : "font-mono tabular-nums font-semibold text-(--text-primary)"
              }
            >
              {formatMoney(closingCents, "BDT", { locale: "en-BD" })}
            </span>
          </div>
        </>
      ) : (
        <div className="rounded-md border border-(--border-subtle) bg-(--bg-canvas) p-10 text-center text-[13px] text-(--text-secondary)">
          <FileText
            className="mx-auto mb-2 h-6 w-6 text-(--text-tertiary)"
            aria-hidden
          />
          Select a dealer above to generate the statement.
        </div>
      )}
    </div>
  );
}
